
import { RevDivider } from '@ds/components/Divider'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { parse } from 'query-string'
import { mapGetters } from 'vuex'

import MyFavoritesToggle from '@dashboard/my-favorites/components/toggle'
import RatingInfo from '@internal-reviews/components/RatingInfo'
import { InstallmentPaymentPrice } from '@payment'
import { ROUTES } from '@router'

import { COUNTRIES_WITHOUT_ORIGINAL_PRICE } from '../../pages/product.constants'
import productTranslations from '../../pages/product.translations'
import AddToCart from '../AddToCart'
import AdvertisedPayment from '../AdvertisedPayment'
import Carriers from '../Carriers'
import CustomizationFunnel from '../CustomizationFunnel'
import Details from '../Details'
import EcoBlockAnchor from '../EcoBlockAnchor'
import NewBattery, { ARROW_POSITIONS } from '../NewBattery'
import Pickers, {
  PICKER_GROUP_WITH_LOAD,
  getColorLabelForSelectedPickers,
  getColorVariantForSelectedPickers,
  getPickerVariantForSelectedPickers,
} from '../Pickers'
import PriceWrapper from '../PriceWrapper'
import ReassuranceButtons from '../ReassuranceButtons'
import ReassuranceItems from '../ReassuranceItems'

export default {
  components: {
    Details,
    AddToCart,
    AdvertisedPayment,
    Carriers,
    CustomizationFunnel,
    EcoBlockAnchor,
    RevDivider,
    InstallmentPaymentPrice,
    MyFavoritesToggle,
    NewBattery,
    Pickers,
    PriceWrapper,
    RatingInfo,
    ReassuranceButtons,
    ReassuranceItems,
  },

  props: {
    product: {
      type: Object,
      required: true,
    },

    offers: {
      type: Object,
      required: true,
    },

    pickers: {
      type: Array,
      default: () => [],
    },

    ratings: {
      type: Object,
      required: true,
    },

    swapOffer: {
      type: Object,
      default: () => ({}),
    },

    swapStatus: {
      type: String,
      required: true,
    },

    trackingInfo: {
      type: Object,
      default: () => ({}),
    },

    technicalSpecifications: {
      type: Object,
      required: true,
    },

    shouldDisplayEcoBlock: {
      type: Boolean,
      required: true,
    },

    shouldDisplayCustomizationFunnel: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      pickersWithLoading: this.getPickersWithLoading(),
    }
  },

  computed: {
    ROUTES: () => ROUTES,
    ...mapGetters({
      country: 'config/country',
      isVatIncluded: 'config/vatIncluded',
      experiments: 'flags/getExperiments',
    }),

    modelTitle() {
      return this.product.rawTitle
    },

    productTranslations: () => productTranslations,

    productId() {
      const { uuid } = this.$route.params

      return uuid
    },

    isAccessory() {
      return this.product.type === 'accessory'
    },

    listingId() {
      return this.offers.activeGrade.id
    },

    price() {
      return this.offers.activeGrade.price
    },

    originalPrice() {
      if (this.shouldDisplayOriginalPrice) {
        return this.product.priceWhenNew
      }

      return {}
    },

    shouldDisplayOriginalPrice() {
      return (
        get(this.offers, 'activeGrade.reassurance.discount.isDisplayed') &&
        !COUNTRIES_WITHOUT_ORIGINAL_PRICE.includes(this.country)
      )
    },

    shipping() {
      return this.offers.activeGrade.reassurance.shipping
    },

    warrantyDelay() {
      return this.offers.activeGrade.reassurance.defaultWarrantyDelay
    },

    seller() {
      return this.offers.activeGrade.merchant
    },

    carriers() {
      return this.product.compatibleCarriers
    },

    activeBackboxGrade() {
      return this.offers.activeBackboxGrade
    },

    grades() {
      return this.offers.grades.map((grade) => {
        return {
          ...grade,
          selected: grade.backboxGrade.value === this.activeBackboxGrade,
        }
      })
    },

    monthlyPaymentPlan() {
      return this.offers.activeGrade.installmentMonthlyPaymentPlan
    },

    shouldDisplayInstallmentPaymentPrice() {
      return (
        this.monthlyPaymentPlan !== null &&
        this.experiments.splitPaymentWording === 'splitPaymentWordingDisplayed'
      )
    },

    shouldDisplayCarriers() {
      return !isEmpty(this.carriers)
    },

    shouldDisplayNewBattery() {
      return this.newBatteryStatus !== 'not-available'
    },

    activeGradeIndex() {
      return this.grades.findIndex(
        ({ backboxGrade }) => backboxGrade.value === this.activeBackboxGrade,
      )
    },

    newBatteryArrowPosition() {
      return ARROW_POSITIONS[this.activeGradeIndex]
    },

    activeGrade() {
      return this.grades[this.activeGradeIndex]
    },

    newBatteryStatus() {
      return this.activeGrade?.newBattery?.status ?? 'not-available'
    },

    newBatteryProps() {
      return {
        deviceName: this.modelTitle,
        disabled: this.newBatteryStatus === 'disabled',
        arrowPosition: this.newBatteryArrowPosition,
        offerType: this.activeGrade.newBattery?.offerType,
      }
    },

    getOfferType() {
      const hash = parse(this.$route.hash)
      const stringOfferType = hash?.offerType || '0'

      return parseInt(stringOfferType, 10)
    },

    shouldDisplayNewDesign() {
      return (
        this.experiments.ppReassuranceNewDesign === 'newDesign' ||
        this.experiments.ppReassuranceNewDesign === 'newDesignMergedInfo'
      )
    },

    shouldDisplayPickers() {
      return !this.shouldDisplayCustomizationFunnel
    },

    priceColor() {
      return getColorVariantForSelectedPickers(this.pickers)
    },

    priceLabel() {
      return getColorLabelForSelectedPickers(this.pickers)
    },

    priceEmphasis() {
      return getPickerVariantForSelectedPickers(this.pickers)
    },
  },
  watch: {
    pickers: async function onPickersChange() {
      this.pickersWithLoading = this.getPickersWithLoading()
    },
  },
  methods: {
    handleNewBatteryChange() {
      const gradePicker = this.pickersWithLoading.find(
        (picker) => picker.id === PICKER_GROUP_WITH_LOAD,
      )

      const selectedGradePicker = gradePicker.items.find(
        (item) => item.selected,
      )

      selectedGradePicker.isLoading = true
    },

    handlePickersChange(id, selectedIndex) {
      const selectedPicker = this.pickersWithLoading.find(
        (picker) => picker.id === id,
      )

      selectedPicker.items[selectedIndex].isLoading = true
    },

    getPickersWithLoading() {
      return this.pickers.map((picker) => ({
        ...picker,
        items: picker.items.map((item) => ({
          ...item,
          isLoading: false,
        })),
      }))
    },
  },
}
